// handlers-news.ts
/**
 * handlers-news.js  —  Kili AI Assistant · News Segment Handlers
 *
 * Import into your existing handlers.js and call the exported functions
 * from the main.js event bindings, or use directly if you prefer the
 * handler module to be standalone.
 *
 * Assumes:
 *  • state    — central state object (extended with newsState fields)
 *  • The AI widget exposes window.AkiliPortal.prefillChat(text) to pre-fill the chat input
 *
 * FIX (_switchToNewsPanel / _switchToChatPanel rewritten):
 *   • Old code used 'ai-chat' (non-existent), set style.display = 'none' on
 *     panels that the app manages with .open class (history/insights), and
 *     never hid the input row or ctx bar — leaving them visible behind the
 *     news feed. These functions now mirror showHistory() / showInsights() in
 *     handlers.js exactly.
 *   • _switchToChatPanel() now dispatches 'akili:switch-to-chat' so main.js
 *     calls showChat(), which is the single authoritative restore path.
 *   • _setNavBtnActive() removed — it conflicted with the existing
 *     _setNavBtns() mechanism that also controls style.display on the buttons.
 */

import { getNews, getNewsArticle, checkBreakingNews, invalidateNewsCache } from './api-news';
import { applyNewsFilters, resetNewsPagination } from './state-news';
import {
  renderNewsList,
  renderNewsCount,
  renderNewsPreview,
  closeNewsPreview,
  showNewsLoading,
  showNewsError,
  setNewsPulseDot,
  setNewsRefreshSpinning,
} from './ui-renderers-news';

/* ── How long to wait before allowing a re-fetch (ms) ── */
const REFETCH_COOLDOWN_MS = 60_000;

/* ── Polling interval for breaking news check (ms) ── */
const BREAKING_POLL_INTERVAL_MS = 5 * 60_000; // every 5 minutes

interface LoadNewsOpts {
  reset?: boolean;
  append?: boolean;
  nocache?: boolean;
}

/* ─────────────────────────────────────────────────────────────────────────────
   showNews()
   Called when the user taps the News icon in the header.
   Mirrors showHistory() / showInsights() pattern.
─────────────────────────────────────────────────────────────────────────────── */

/**
 * @param {Object} state  — the central assistant state object
 */
export async function showNews(state) {
  /* 1. Set view flag (mirrors state.view = 'history' pattern) */
  state.view = 'news';

  /* 2. Hide all other panels, show news panel */
  _switchToNewsPanel();

  /* 3. Load articles if cache is stale */
  const stale = Date.now() - (state.newsLastFetched ?? 0) > REFETCH_COOLDOWN_MS;
  if (stale || !state.newsArticlesRaw.length) {
    await loadNews(state, { reset: true });
  } else {
    /* Re-render from in-memory state */
    _renderFromState(state);
  }

  /* 4. Start breaking-news polling (if not already running) */
  _ensureBreakingPoll(state);
}

/* ─────────────────────────────────────────────────────────────────────────────
   loadNews()
   Fetches articles from the API and updates the panel.
─────────────────────────────────────────────────────────────────────────────── */

export async function loadNews(state: any, opts: LoadNewsOpts = {}) {
  const { reset = false, append = false, nocache = false } = opts;

  if (state.newsLoading) return; // prevent concurrent fetches
  state.newsLoading = true;

  if (reset) {
    resetNewsPagination(state);
    showNewsLoading(true);
  } else {
    /* Load-more path: show a subtle spinner on the button */
    const loadMoreBtn = document.getElementById('ai-news-load-more');
    if (loadMoreBtn) loadMoreBtn.textContent = 'Loading…';
  }

  setNewsRefreshSpinning(true);

  try {
    const offset = state.newsPage * state.newsLimit;

    const data = await getNews({
      limit:   state.newsLimit,
      offset,
      region:  state.newsFilters.region  !== 'all' ? state.newsFilters.region  : undefined,
      docType: state.newsFilters.docType !== 'all' ? state.newsFilters.docType : undefined,
      nocache,
    });

    const articles = data.articles ?? [];

    /* Accumulate raw articles (for client-side search without re-fetching) */
    if (reset) {
      state.newsArticlesRaw = articles;
    } else {
      state.newsArticlesRaw = [...(state.newsArticlesRaw ?? []), ...articles];
    }

    state.newsTotal       = data.total ?? articles.length;
    state.newsHasMore     = (offset + articles.length) < state.newsTotal;
    state.newsError       = null;
    state.newsLastFetched = Date.now();

    /* Apply client-side text search */
    applyNewsFilters(state);

    renderNewsList(state.newsArticles, { append: append && !reset });
    renderNewsCount(state.newsArticles.length, state.newsTotal);

    /* Show/hide load-more button */
    const footer = document.getElementById('ai-news-footer');
    if (footer) footer.style.display = state.newsHasMore ? '' : 'none';

  } catch (err) {
    console.error('[news] loadNews failed:', err);
    state.newsError = err.message ?? 'Unknown error';
    showNewsError(state.newsError);
  } finally {
    state.newsLoading = false;
    setNewsRefreshSpinning(false);

    const loadMoreBtn = document.getElementById('ai-news-load-more');
    if (loadMoreBtn) loadMoreBtn.textContent = 'Load more articles';
  }
}

/* ─────────────────────────────────────────────────────────────────────────────
   refreshNews()
   Manual refresh — invalidates cache and re-fetches.
─────────────────────────────────────────────────────────────────────────────── */

export async function refreshNews(state) {
  invalidateNewsCache();
  closeNewsPreview();
  await loadNews(state, { reset: true, nocache: true });
}

/* ─────────────────────────────────────────────────────────────────────────────
   loadMoreNews()
   Pagination — appends next page.
─────────────────────────────────────────────────────────────────────────────── */

export async function loadMoreNews(state) {
  if (!state.newsHasMore || state.newsLoading) return;
  state.newsPage = (state.newsPage ?? 0) + 1;
  await loadNews(state, { append: true });
}

/* ─────────────────────────────────────────────────────────────────────────────
   setNewsRegionFilter() / setNewsDocTypeFilter()
─────────────────────────────────────────────────────────────────────────────── */

/**
 * @param {Object} state
 * @param {string} region  — 'all' | 'kenya' | 'africa' | 'global'
 */
export async function setNewsRegionFilter(state, region) {
  if (state.newsFilters.region === region) return;
  state.newsFilters.region = region;
  _syncFilterPills('region', region);
  /* Re-fetch from API with new filter to get accurate total */
  resetNewsPagination(state);
  await loadNews(state, { reset: true });
}

/**
 * @param {Object} state
 * @param {string} docType
 */
export async function setNewsDocTypeFilter(state, docType) {
  if (state.newsFilters.docType === docType) return;
  state.newsFilters.docType = docType;
  _syncFilterPills('doctype', docType);
  resetNewsPagination(state);
  await loadNews(state, { reset: true });
}

/* ─────────────────────────────────────────────────────────────────────────────
   handleNewsSearch()
   Client-side keyword filter (no API call — filters newsArticlesRaw).
─────────────────────────────────────────────────────────────────────────────── */

let _searchDebounce = null;

export function handleNewsSearch(state, query) {
  state.newsFilters.search = query ?? '';

  /* Show/hide clear button */
  const clearBtn = document.getElementById('ai-news-search-clear');
  if (clearBtn) clearBtn.style.display = query ? '' : 'none';

  clearTimeout(_searchDebounce);
  _searchDebounce = setTimeout(() => {
    applyNewsFilters(state);
    renderNewsList(state.newsArticles, { append: false });
    renderNewsCount(state.newsArticles.length, state.newsTotal);
  }, 220);
}

/* ─────────────────────────────────────────────────────────────────────────────
   openNewsPreview()
   Slides in the inline article detail drawer.
─────────────────────────────────────────────────────────────────────────────── */

export async function openNewsPreview(state, articleId) {
  /* Optimistically render from list cache first */
  const cached = (state.newsArticlesRaw ?? []).find(a => a.id === articleId);
  if (cached) {
    state.newsPreviewArticle = cached;
    renderNewsPreview(cached);
  }

  /* Attempt to fetch full content in background */
  try {
    const full = await getNewsArticle(articleId);
    state.newsPreviewArticle = full;
    renderNewsPreview(full);
  } catch {
    /* Silently keep the cached summary version */
  }
}

/* ─────────────────────────────────────────────────────────────────────────────
   askKiliAboutArticle()
   Pre-fills the chat input and switches to chat view.
─────────────────────────────────────────────────────────────────────────────── */

export function askKiliAboutArticle(state, articleTitle) {
  const prompt = `Summarise and analyse this article for me: "${articleTitle}"`;

  /* Pre-fill the chat textarea (direct DOM approach) */
  const chatInput = (
    document.getElementById('ai-input') ||
    document.querySelector('.ai-chat-input, [data-role="chat-input"]')
  ) as HTMLInputElement | null;
  if (chatInput) {
    chatInput.value = prompt;
    chatInput.dispatchEvent(new Event('input', { bubbles: true }));
    chatInput.focus();
  }

  /* Also try the portal's public API */
  if (window.AkiliPortal?.prefillChat) {
    window.AkiliPortal.prefillChat(prompt);
  }

  /* Switch back to chat view — close preview first */
  closeNewsPreview();
  state.view = 'chat';
  _switchToChatPanel();
}

/* ─────────────────────────────────────────────────────────────────────────────
   Breaking-news polling
─────────────────────────────────────────────────────────────────────────────── */

export async function pollBreakingNews(state) {
  const result = await checkBreakingNews();
  state.newsHasBreaking = result.breaking ?? false;
  setNewsPulseDot(state.newsHasBreaking);
}

/* ─────────────────────────────────────────────────────────────────────────────
   Teardown (call when widget is closed)
─────────────────────────────────────────────────────────────────────────────── */

export function teardownNews(state) {
  clearInterval(state.newsPollingTimer);
  state.newsPollingTimer = null;
  clearTimeout(_searchDebounce);
}

/* ─────────────────────────────────────────────────────────────────────────────
   Private helpers
─────────────────────────────────────────────────────────────────────────────── */

/**
 * Show the news panel and hide every other content area.
 *
 * FIX: Mirrors showHistory() / showInsights() in handlers.js exactly:
 *   • Adds .hidden to #ai-msgs-container  (was incorrectly targeting #ai-chat)
 *   • Removes .open from #ai-history and #ai-insights  (not style.display)
 *   • Removes .open from #ai-workflows and #ai-personalfacts if present
 *   • Hides #ai-ctx-bar and #ai-input-row  (previously never done)
 *   • Hides all nav buttons and shows back-btn  (mirrors _setNavBtns('hide'))
 *   • Reveals #ai-news via style.display = ''  (panel starts with display:none)
 */
function _switchToNewsPanel() {
  // ── Hide chat container ─────────────────────────────────────────────────
  const msgsContainer = document.getElementById('ai-msgs-container');
  if (msgsContainer) msgsContainer.classList.add('hidden');

  // ── Close any open sibling panels (history, insights, workflows, personal facts) ─
  ['ai-history', 'ai-insights', 'ai-workflows', 'ai-personalfacts'].forEach(id => {
    const el = document.getElementById(id);
    if (el) el.classList.remove('open');
  });

  // ── Hide all other drawer chrome elements that would otherwise push the news panel down ─
  const elementsToHide = [
    'ai-ctx-bar',           // context bar
    'ai-proactive-panel',   // proactive suggestions panel
    'ps-pill',              // collapsed proactive pill
    'ai-doc-chip',          // document attachment chip
    'ai-mode-bar',          // chat/draft/prep mode toggle
    'ai-input-row',         // message input area
    'ai-privacy-btn',       // "What Kili remembers" button
    'ai-disclaimer'         // disclaimer text (class? adjust if ID)
  ];
  elementsToHide.forEach(id => {
    const el = document.getElementById(id);
    if (el) el.style.display = 'none';
  });

  // Also hide the disclaimer by class if it doesn't have an ID
  const disclaimer = document.querySelector('.ai-disclaimer') as HTMLElement | null;
  if (disclaimer) disclaimer.style.display = 'none';

  // ── Show back button; hide all other nav buttons (mirrors _setNavBtns('hide')) ─
  document.querySelectorAll('.ai-hdr-btn').forEach(btn => {
    (btn as HTMLElement).style.display = (btn.id === 'ai-back-btn') ? 'flex' : 'none';
  });

  // ── Reveal the news panel as a flex child ─
  const newsPanel = document.getElementById('ai-news');
  if (newsPanel) newsPanel.style.display = 'flex';   // 'flex' not '' – it's a flex container
}

/**
 * Return to chat view from the news panel.
 *
 * FIX: The old implementation tried to show '#ai-chat' (which does not exist)
 * and never restored the nav buttons, input row, or context bar.
 * Dispatching 'akili:switch-to-chat' delegates to showChat() in handlers.js
 * which is the single authoritative restore path for all these elements.
 * main.js must listen for this event and call showChat() — add once to
 * bindCustomEvents():
 *
 *   document.addEventListener('akili:switch-to-chat', () => showChat());
 */
function _switchToChatPanel() {
  document.dispatchEvent(new CustomEvent('akili:switch-to-chat'));
}

function _syncFilterPills(group, value) {
  document.querySelectorAll(`.ai-news-filter-pill[data-group="${group}"]`).forEach(pill => {
    const el = pill as HTMLElement;
    const pillVal = group === 'region' ? el.dataset.region : el.dataset.doctype;
    el.classList.toggle('active', pillVal === value);
  });
}

function _renderFromState(state) {
  applyNewsFilters(state);
  renderNewsList(state.newsArticles, { append: false });
  renderNewsCount(state.newsArticles.length, state.newsTotal);
  const footer = document.getElementById('ai-news-footer');
  if (footer) footer.style.display = state.newsHasMore ? '' : 'none';
}

function _ensureBreakingPoll(state) {
  if (state.newsPollingTimer) return; // already running
  pollBreakingNews(state); // immediate check
  state.newsPollingTimer = setInterval(
    () => pollBreakingNews(state),
    BREAKING_POLL_INTERVAL_MS,
  );
}
